import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { emailValidationSchema } from "../../validation/emailSchema";
import prodigy from "../../Assets/brand/ProdiogyIcon.svg";
import eyeOpen from "../../Assets/icons/eyeOpen.svg";
import eyeClosed from "../../Assets/icons/eyeClose.svg";
import { LoginPayload, ActiveSession } from "../../Interfaces/LoginInterface";
import { useAppDispatch } from "../../redux/hooks";
import { toast } from "sonner";
import { LoginApi } from "../../Apis/login";
import { clearTokens } from "../../redux/slices/AuthSlice";
import { handleLoginSuccess } from "../../auth/handleLoginSuccess";
import { getOrCreateDeviceId } from "../../utils/deviceId";
import { colorTokens } from "../../designTokens";
import { TextField } from "@mui/material";

// Login form validation schema
const loginValidationSchema = emailValidationSchema.shape({
  password: yup.string().required("Password is required"),
});

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [invalidCredentialsError, setInvalidCredentialsError] = useState(false);
  const [sessionConflict, setSessionConflict] = useState<
    ActiveSession[] | null
  >(null);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      // Reset invalid credentials error on new submit
      setInvalidCredentialsError(false);

      if (values.email && values.password) {
        try {
          const payload: LoginPayload = {
            password: values.password,
            deviceId: getOrCreateDeviceId(),
            identifier: values.email || "",
          };

          const loginResponse = await LoginApi(payload);
          await handleLoginSuccess(dispatch, navigate, loginResponse);
        } catch (error) {
          dispatch(clearTokens());

          const err = error as {
            response?: {
              status?: number;
              data?: {
                error?: string;
                message?: string;
                sessionConflict?: boolean;
                activeSessions?: ActiveSession[];
              };
            };
          };
          const status = err?.response?.status;
          const data = err?.response?.data;
          const errorCode = typeof data?.error === "string" ? data.error : "";
          const errorMessage =
            typeof data?.message === "string" ? data.message : errorCode;

          // Handle 409 session conflict (single-session mode)
          if (status === 409 && data?.sessionConflict === true) {
            setSessionConflict(
              Array.isArray(data.activeSessions) ? data.activeSessions : []
            );
            return;
          }

          // Handle "Too many attempts" (backend: TOO_MANY_ATTEMPTS)
          if (
            errorCode === "TOO_MANY_ATTEMPTS" ||
            (errorMessage && /too many attempts/i.test(errorMessage))
          ) {
            toast.error(
              errorMessage ||
                "Too many attempts. We've sent a password reset email."
            );
            return;
          }

          // Handle "Invalid credentials" (backend: INVALID_CREDENTIALS)
          if (
            errorCode === "INVALID_CREDENTIALS" ||
            (errorMessage && /invalid credentials/i.test(errorMessage))
          ) {
            setInvalidCredentialsError(true);
            toast.error("Incorrect email or password.");
            return;
          }

          // Handle "Email not verified"
          if (errorCode === "EMAIL_NOT_VERIFIED") {
            toast.error(
              "Email not verified. A verification code has been sent to your email."
            );
            return;
          }

          // Show backend message when available, otherwise generic
          toast.error(errorMessage || "Incorrect email or password.");
        }
      }
    },
  });

  const handleForgotPassword = () => {
    navigate("/forgot-password", {
      state: { email: formik.values.email || "" },
    });
  };

  const handleForceLogin = async () => {
    const email = formik.values.email || "";
    const password = formik.values.password || "";
    if (!email || !password) return;
    setForceLoginLoading(true);
    try {
      const payload: LoginPayload = {
        identifier: email,
        password,
        deviceId: getOrCreateDeviceId(),
        forceLogin: true,
      };
      const loginResponse = await LoginApi(payload);
      setSessionConflict(null);
      await handleLoginSuccess(dispatch, navigate, loginResponse);
    } catch (error) {
      setSessionConflict(null);
      const err = error as {
        response?: {
          status?: number;
          data?: {
            error?: string;
            message?: string;
            sessionConflict?: boolean;
            activeSessions?: ActiveSession[];
          };
        };
      };
      const data = err?.response?.data;
      const errorCode = typeof data?.error === "string" ? data.error : "";
      const errorMessage =
        typeof data?.message === "string" ? data.message : errorCode;
      if (
        errorCode === "TOO_MANY_ATTEMPTS" ||
        (errorMessage && /too many attempts/i.test(errorMessage))
      ) {
        toast.error(
          errorMessage ||
            "Too many attempts. We've sent a password reset email."
        );
      } else if (
        errorCode === "INVALID_CREDENTIALS" ||
        (errorMessage && /invalid credentials/i.test(errorMessage))
      ) {
        setInvalidCredentialsError(true);
        toast.error("Incorrect email or password.");
      } else if (errorCode === "EMAIL_NOT_VERIFIED") {
        toast.error(
          "Email not verified. A verification code has been sent to your email."
        );
      } else {
        toast.error(errorMessage || "Incorrect email or password.");
      }
    } finally {
      setForceLoginLoading(false);
    }
  };

  return (
    <div id="login-container" className="w-full max-w-md h-full flex flex-col">
      <div id="login-content" className="flex-1 flex flex-col justify-center">
        {/* Logo */}
        <div id="login-logo-container" className="mb-10">
          <img
            id="login-logo"
            src={prodigy}
            alt="Prodigy Health"
            className="h-auto w-32 md:w-40 lg:w-48"
          />
        </div>

        {/* Heading */}
        <h1
          id="login-heading"
          className="text-[18px] font-medium text-[#080F1A] font-outfit leading-[120%] mb-2"
        >
          Welcome to Carepro
        </h1>
        <p
          id="login-description"
          className="text-[14px] font-normal text-[#647491] font-outfit leading-[150%] mb-8"
        >
          Enter your email and password to login
        </p>

        {/* Session conflict UI (replaces form when active) */}
        {sessionConflict !== null ? (
          <div
            id="login-session-conflict"
            className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]"
          >
            <p className="text-[14px] font-normal text-[#080F1A] font-outfit leading-[150%] mb-3">
              You’re logged in elsewhere. Do you want to continue here instead?
            </p>
            <div className="flex gap-3">
              <button
                id="login-session-conflict-cancel"
                type="button"
                onClick={() => setSessionConflict(null)}
                disabled={forceLoginLoading}
                className="flex-1 py-2.5 rounded-lg border border-[#0566FF] text-[#0566FF] text-[14px] font-medium font-outfit disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                id="login-session-conflict-continue"
                type="button"
                onClick={handleForceLogin}
                disabled={forceLoginLoading}
                className="flex-1 py-2.5 rounded-lg bg-[#0566FF] text-white text-[14px] font-medium font-outfit disabled:opacity-50"
              >
                {forceLoginLoading ? "Continuing…" : "Continue"}
              </button>
            </div>
          </div>
        ) : (
          <form
            id="login-form"
            onSubmit={formik.handleSubmit}
            className="flex flex-col space-y-5"
          >
            {/* Email */}

            <div id="login-email-container" className="relative flex flex-col">
              <TextField
                id="login-email-input"
                label="Email address"
                type="text"
                variant="outlined"
                fullWidth
                {...formik.getFieldProps("email")}
                error={
                  (formik.touched.email && Boolean(formik.errors.email)) ||
                  invalidCredentialsError
                }
                helperText={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : invalidCredentialsError
                      ? "Incorrect email or password"
                      : ""
                }
                aria-label="Email address"
                aria-required="true"
                aria-invalid={
                  (formik.touched.email && !!formik.errors.email) ||
                  invalidCredentialsError
                }
                aria-describedby={
                  (formik.touched.email && formik.errors.email) ||
                  invalidCredentialsError
                    ? "login-email-error"
                    : undefined
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                      WebkitTextFillColor: "#000",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                  },

                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },

                  /*  Focused (normal) */
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0566FF",
                  },

                  /*  Error (not focused) */
                  "& .MuiInputLabel-root.Mui-error": {
                    color: colorTokens["Alert/Red/Dark"],
                  },

                  /*  Error + Focused (THIS is the missing part) */
                  "& .MuiInputLabel-root.Mui-error.Mui-focused": {
                    color: colorTokens["Alert/Red/Dark"],
                  },

                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "#0566FF",
                    borderWidth: "1px",
                  },

                  "& .MuiOutlinedInput-root.Mui-error fieldset": {
                    borderColor: colorTokens["Alert/Red/Dark"],
                  },
                }}
              />

              {/* ❗ Keep error container ID for accessibility/testing */}
              {/* {(formik.touched.email && formik.errors.email) && (
              <div
                id="login-email-error"
                className="mt-1 text-xs text-[#F94839] font-normal font-outfit"
                role="alert"
              >
                {formik.errors.email}
              </div>
            )} */}
            </div>

            {/* Password */}
            <div
              id="login-password-container"
              className="relative flex flex-col"
            >
              <TextField
                id="login-password-input"
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                {...formik.getFieldProps("password")}
                error={
                  (formik.touched.password &&
                    Boolean(formik.errors.password)) ||
                  invalidCredentialsError
                }
                helperText={
                  formik.touched.password && formik.errors.password
                    ? formik.errors.password
                    : invalidCredentialsError
                      ? "Incorrect email or password"
                      : ""
                }
                aria-label="Password"
                aria-required="true"
                aria-invalid={
                  (formik.touched.password && !!formik.errors.password) ||
                  invalidCredentialsError
                }
                aria-describedby={
                  (formik.touched.password && formik.errors.password) ||
                  invalidCredentialsError
                    ? "login-password-error"
                    : undefined
                }
                InputProps={{
                  endAdornment: (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <img
                        src={showPassword ? eyeOpen : eyeClosed}
                        alt=""
                        className="w-5 h-5"
                      />
                    </button>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                      WebkitTextFillColor: "#000",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                  },

                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },

                  /*  Focused (normal) */
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0566FF",
                  },

                  /*  Error (not focused) */
                  "& .MuiInputLabel-root.Mui-error": {
                    color: colorTokens["Alert/Red/Dark"],
                  },

                  /*  Error + Focused (THIS is the missing part) */
                  "& .MuiInputLabel-root.Mui-error.Mui-focused": {
                    color: colorTokens["Alert/Red/Dark"],
                  },

                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "#0566FF",
                    borderWidth: "1px",
                  },

                  "& .MuiOutlinedInput-root.Mui-error fieldset": {
                    borderColor: colorTokens["Alert/Red/Dark"],
                  },
                }}
              />

              {/* ❗ Keep error container ID for accessibility/testing */}
              {/* {((formik.touched.password && formik.errors.password) ||
              invalidCredentialsError) && (
                <div
                  id="login-password-error"
                  className="mt-1 text-xs text-[#F94839] font-normal font-outfit"
                  role="alert"
                >
                  {invalidCredentialsError
                    ? "Incorrect email or password"
                    : formik.errors.password}
                </div>
              )} */}
            </div>

            {/* Forgot password */}
            <div
              id="login-forgot-password-container"
              className="flex justify-end"
            >
              <button
                id="login-forgot-password-button"
                type="button"
                onClick={handleForgotPassword}
                className="text-[14px] font-medium text-[#0566FF] font-outfit leading-[150%]"
              >
                Forgot password?
              </button>
            </div>

            {/* Login Button */}
            <button
              id="login-submit-button"
              type="submit"
              className="w-full bg-[#0566FF] text-white text-center text-[14px] font-medium font-outfit leading-[150%] py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                !formik.values.email ||
                !formik.values.password ||
                !formik.isValid
              }
            >
              Login
            </button>
          </form>
        )}
      </div>

      <p id="login-footer" className="mt-auto mb-10 text-xs text-[#647491]">
        © 2025 Arete Healthtech Pvt. Ltd. All rights reserved.
      </p>
    </div>
  );
};

export default Login;
